import type { Comment, CreateComment, Page, UpdateComment } from '@servicetim/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { portalPath, useApiContext } from './client';
import { DEFAULT_PAGE_SIZE, pageQuery, usePagedList } from './paging';

const commentsPath = (ticketId: string) => portalPath(`/tickets/${ticketId}/comments`);

/** `GET /v1/tickets/{id}/comments` (keyset pages, oldest first). */
export function useTicketComments(ticketId: string | undefined, pageSize = DEFAULT_PAGE_SIZE) {
  const { request, keys } = useApiContext();
  return usePagedList<Comment>({
    queryKey: [...keys.ticket(ticketId ?? '').comments, { pageSize }],
    fetchPage: (cursor, signal) =>
      request<Page<Comment>>('GET', commentsPath(ticketId ?? ''), {
        query: pageQuery(cursor, pageSize),
        signal,
      }),
    enabled: ticketId !== undefined,
  });
}

export function useTicketComment(ticketId: string | undefined, commentId: string | undefined) {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: [...keys.ticket(ticketId ?? '').comments, 'detail', commentId ?? ''],
    queryFn: ({ signal }) =>
      request<Comment>('GET', `${commentsPath(ticketId ?? '')}/${commentId}`, { signal }),
    enabled: ticketId !== undefined && commentId !== undefined,
  });
}

/** `POST /v1/tickets/{id}/comments`; `visibility: 'internal'` needs `comment.internal`. */
export function useCreateComment(ticketId: string) {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: (input: Partial<CreateComment>) =>
      request<Comment>('POST', commentsPath(ticketId), { body: input }),
    onSuccess: () =>
      invalidate(
        keys.ticket(ticketId).comments,
        keys.ticket(ticketId).history,
        keys.ticket(ticketId).detail,
      ),
  });
}

/** `PATCH /v1/tickets/{id}/comments/{cid}` (body only). */
export function useUpdateComment(ticketId: string) {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: ({ id, ...input }: UpdateComment & { id: string }) =>
      request<Comment>('PATCH', `${commentsPath(ticketId)}/${id}`, { body: input }),
    onSuccess: () => invalidate(keys.ticket(ticketId).comments, keys.ticket(ticketId).history),
  });
}

export function useDeleteComment(ticketId: string) {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: (id: string) => request<void>('DELETE', `${commentsPath(ticketId)}/${id}`),
    onSuccess: () => invalidate(keys.ticket(ticketId).comments, keys.ticket(ticketId).history),
  });
}
